import React, { useState, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import { Add, Remove } from '@mui/icons-material';
import { Grid, IconButton, TableCell, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { IAssignedMaterial, IMaterial } from '../../../interfaces';
import { MaterialDetailsDialog } from '../../MaterialDetailsDialog';
import './index.scss';

interface IGroupedMaterialRow {
    material: IMaterial,
    valid: boolean,
    assigned: IAssignedMaterial[],
    onAdjustQuantity: (materialId: string | undefined, subtract: boolean) => void,
    onSetQuantity: (materialId: string | undefined, qty: number) => void,
    readonly: boolean
}

export const GroupedMaterialRow: React.FC<IGroupedMaterialRow> = ({
    valid,
    onAdjustQuantity,
    onSetQuantity,
    assigned,
    readonly,
    material,
    ...props
}) => {
    const [hideTooltip, setHideTooltip] = useState(false);
    const [debouncedQuantity, setDebouncedQuantity] = useState(() => {
        // Initialize state based on the current assignment
        return assigned.find(a => a.id === material.materialId)?.quantity ?? 0;
    });

    // Function to get row style based on material restrictions
    const getRowStyle = () => {
        const isRestricted = material.hasUsageRestrictions === "Yes";
        const isNonPorterbrook = material.ownedBy?.toUpperCase() !== "PORTERBROOK";
        return (isRestricted || isNonPorterbrook) ? { backgroundColor: "#ed6c0230" } : {};
    };

    // Function to get tooltip text
    const getTooltipText = () => {
        if (hideTooltip) return "";
        const isRestricted = material.hasUsageRestrictions === "Yes";
        const isNonPorterbrook = material.ownedBy?.toUpperCase() !== "PORTERBROOK";
        return isRestricted ? material.usageRestrictions :
            isNonPorterbrook ? `Owned by ${material.ownedBy}.` : "";
    };

    const debouncedUpdate = useCallback(debounce((newQty) => {
        console.log("setting quantity", newQty);
        onSetQuantity(material.materialId, newQty);
    }, 500), [material.materialId, onSetQuantity]);

    const handleAdjust = useCallback((decrement: boolean) => {
        setDebouncedQuantity(prevQty => {
            const adjustedQty = decrement ? prevQty - 1 : prevQty + 1;
            const newQty = Math.max(0, adjustedQty); // Ensure quantity doesn't go below 0
            debouncedUpdate(newQty);
            return newQty;
        });
    }, [debouncedUpdate]);

    useEffect(() => {
        const currentQty = assigned.find(a => a.id === material.materialId)?.quantity ?? 0;
        setDebouncedQuantity(currentQty);
    }, [assigned, material.materialId]);

    return (
        <Tooltip title={getTooltipText()}>
            <TableRow key={material.materialId} style={getRowStyle()} onMouseEnter={() => setHideTooltip(false)} onMouseLeave={() => setHideTooltip(true)}>
                <TableCell>
                    <Typography noWrap fontSize="small">
                        {(material.site ? material.site.value : "-") + " " + (material.warehouse ? material.warehouse.value : "-")}
                    </Typography>
                </TableCell>
                <TableCell>{material.rack ? material.rack : "-"}</TableCell>
                <TableCell>{material.row ? material.row : "-"}</TableCell>
                <TableCell>{material.pallet ? material.pallet : "-"}</TableCell>
                <TableCell>{material.serialNumber ? material.serialNumber : "-"}</TableCell>
                {material.statusId === 3 && <TableCell>{material.dispatchRequestReference}</TableCell>}
                <TableCell onMouseEnter={() => setHideTooltip(true)} onMouseLeave={() => setHideTooltip(false)}>
                    <MaterialDetailsDialog material={material} viewMode="search_result" />
                </TableCell>
                <TableCell>
                    <Grid container justifyContent="space-between">
                        <Grid item display="flex" flexDirection="row" alignItems="center">
                            <TextField
                                type="number"
                                variant="standard"
                                inputProps={{
                                    min: 0,
                                    max: material.quantity ?? 0,
                                    style: { textAlign: 'right' },
                                }}
                                disabled={readonly}
                                sx={{ width: '4em' }}
                                value={debouncedQuantity}
                                onChange={(e) => setDebouncedQuantity(Number(e.target.value))}
                                onBlur={() => debouncedUpdate(debouncedQuantity)}
                            /><div>/{material.quantity ? material.quantity : "0"}</div>
                        </Grid>
                        {!readonly && (
                            <Grid item>
                                <IconButton onClick={() => handleAdjust(true)}><Remove /></IconButton>
                                <IconButton onClick={() => handleAdjust(false)}><Add /></IconButton>
                            </Grid>
                        )}
                    </Grid>
                </TableCell>
            </TableRow>
        </Tooltip>
    );
};
